import { useT } from "@transifex/react";
import { useId, useState, type FormEvent } from "react";
import InlineFormError from "../../basic/InlineFormError";
import { MAX_NAME_LENGTH } from "../../constants/LimitConstants";
import {
    LOGIN_COLUMN_CLASS,
    PRIMARY_PILL_BUTTON_CLASS,
    SECONDARY_PILL_BUTTON_CLASS,
    TEXT_INPUT_CLASS,
} from "./classMemberFormStyles";
import { JoinErrorVariants } from "./useClassMemberJoin";

interface JoinClassFormProps {
    className: string;
    teacherDisplayName: string;
    /** Set once a member exists: the name is shown read-only so a retry repeats only the login. */
    lockedName?: string;
    isSubmitting: boolean;
    error: JoinErrorVariants;
    onSubmit: (name: string) => void;
    onBack: () => void;
}

const JoinClassForm = (props: JoinClassFormProps) => {
    const t = useT();
    const nameInputId = useId();

    const [typedName, setTypedName] = useState("");
    const name = props.lockedName || typedName;

    const t_title = t("Join the classroom", { _tags: "ClassMemberLogin, JoinClassForm" });
    const t_nameLabel = t("Your name", { _tags: "ClassMemberLogin, JoinClassForm, ScreenReaderOnly" });
    const t_classWithTeacher = t("{className} with {teacherName}", {
        _tags: "ClassMemberLogin, JoinClassForm",
        className: props.className,
        teacherName: props.teacherDisplayName,
    });
    const t_namePlaceholder = t('Your name, e.g. "Mimi M."', { _tags: "ClassMemberLogin, JoinClassForm" });
    const t_letsGo = t("Let's go!", { _tags: "ClassMemberLogin, JoinClassForm" });
    const t_back = t("Back", { _tags: "ClassMemberLogin, JoinClassForm" });
    const t_errorRejected = t("You can't join this class right now. Please ask your teacher.", {
        _tags: "ClassMemberLogin, JoinClassForm",
    });
    const t_errorTransient = t("Joining isn't possible right now. Please try again.", {
        _tags: "ClassMemberLogin, JoinClassForm",
    });

    const trimmedName = name.trim();
    const canSubmit = !props.isSubmitting && trimmedName !== "";
    const subline = props.teacherDisplayName ? t_classWithTeacher : props.className;

    const getErrorText = () => {
        switch (props.error) {
            case JoinErrorVariants.NONE:
                return null;
            case JoinErrorVariants.REJECTED:
                return t_errorRejected;
            case JoinErrorVariants.TRANSIENT:
                return t_errorTransient;
        }
    };

    const errorText = getErrorText();

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!canSubmit) return;
        props.onSubmit(trimmedName);
    };

    return (
        <form
            onSubmit={handleSubmit}
            className={`${LOGIN_COLUMN_CLASS} gap-xxl`}
            data-testid="join-class-form"
        >
            <div className="flex w-full flex-col items-center gap-xs text-center">
                <h1 className="text-xxl font-semibold text-p6-grey-dark break-words">{t_title}</h1>
                <p className="text-m text-p6-grey-dark break-words">{subline}</p>
            </div>
            <div className="flex w-full flex-col items-center gap-s">
                <label
                    htmlFor={nameInputId}
                    className="sr-only"
                >
                    {t_nameLabel}
                </label>
                <input
                    id={nameInputId}
                    type="text"
                    value={name}
                    onChange={(e) => setTypedName(e.target.value)}
                    readOnly={Boolean(props.lockedName)}
                    placeholder={t_namePlaceholder}
                    maxLength={MAX_NAME_LENGTH}
                    autoComplete="off"
                    spellCheck={false}
                    aria-invalid={Boolean(errorText)}
                    data-testid="join-class-name-input"
                    className={TEXT_INPUT_CLASS}
                />
                {errorText && <InlineFormError isLarge>{errorText}</InlineFormError>}
                <button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid="button-join-class-submit"
                    className={PRIMARY_PILL_BUTTON_CLASS}
                >
                    {t_letsGo}
                </button>
                <button
                    type="button"
                    onClick={props.onBack}
                    data-testid="button-join-class-back"
                    className={SECONDARY_PILL_BUTTON_CLASS}
                >
                    {t_back}
                </button>
            </div>
        </form>
    );
};

export default JoinClassForm;
